import React from "react";

// Standalone visual preview of palette options (mockups only).
const options = [
  {
    id: "A",
    name: "Warm Ivory / Editorial",
    note: "Light, airy, magazine-like. Easiest on the eyes.",
    bg: "#F7F4EF",
    panel: "#FFFFFF",
    text: "#191714",
    muted: "#6B6660",
    accent: "#C1121F",
    btnText: "#FFFFFF",
    border: "#E6E0D5",
    hero: false,
  },
  {
    id: "B",
    name: "Soft Charcoal",
    note: "Bold and premium, but warmer and lighter than pure black.",
    bg: "#1C1B1A",
    panel: "#232220",
    text: "#EEEAE3",
    muted: "#A39F98",
    accent: "#E24A4A",
    btnText: "#FFFFFF",
    border: "rgba(255,255,255,0.10)",
    hero: true,
  },
  {
    id: "C",
    name: "Clean Light Grey",
    note: "Crisp and corporate. White and soft-grey sections.",
    bg: "#EFEEEB",
    panel: "#FFFFFF",
    text: "#141414",
    muted: "#6E6E6E",
    accent: "#D11F27",
    btnText: "#FFFFFF",
    border: "#E1E0DC",
    hero: false,
  },
  {
    id: "D",
    name: "Two-tone (Ivory + Charcoal feature)",
    note: "Mostly ivory, with dramatic dark feature sections.",
    bg: "#F5F2EC",
    panel: "#18191C",
    text: "#191714",
    muted: "#6B6660",
    accent: "#C1121F",
    btnText: "#FFFFFF",
    border: "#E6E0D5",
    hero: "split",
  },
];

function Mock({ o }) {
  const dark = o.hero === true;
  return (
    <div
      className="rounded-[1.4rem] overflow-hidden border"
      style={{ background: o.bg, borderColor: o.border }}
    >
      {/* mini top bar */}
      <div
        className="flex items-center justify-between px-6 h-14 border-b"
        style={{ borderColor: o.border, background: dark ? o.bg : o.panel }}
      >
        <div style={{ color: o.text }} className="font-semibold tracking-tight">
          Nitin Gupta
          <span
            className="ml-2 text-[0.55rem] tracking-[0.25em] uppercase align-middle"
            style={{ color: o.muted }}
          >
            Double Your Exports
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-5 text-xs" style={{ color: o.muted }}>
          <span>About</span>
          <span>Solutions</span>
          <span>VPV</span>
          <span
            className="px-4 py-2 rounded-full text-[0.7rem] font-semibold"
            style={{ background: o.accent, color: o.btnText }}
          >
            Book a Call
          </span>
        </div>
      </div>

      {o.hero === "split" ? (
        <div className="grid md:grid-cols-2">
          {/* ivory side */}
          <div className="p-8">
            <div
              className="text-[0.6rem] tracking-[0.3em] uppercase font-bold"
              style={{ color: o.accent }}
            >
              By invitation only
            </div>
            <h3
              className="mt-4 font-serif-display text-4xl font-bold leading-[1.02]"
              style={{ color: o.text }}
            >
              Stand Out.<br />
              <span style={{ color: o.accent }}>Sell More.</span>
            </h3>
            <p className="mt-4 text-sm" style={{ color: o.muted }}>
              Build trust and close deals faster than ever.
            </p>
            <span
              className="inline-block mt-6 px-5 py-2.5 rounded-full text-sm font-semibold"
              style={{ background: o.accent, color: o.btnText }}
            >
              Book a Strategy Call
            </span>
          </div>
          {/* dark feature side */}
          <div className="p-8 flex flex-col justify-center" style={{ background: o.panel }}>
            <div className="text-[0.6rem] tracking-[0.3em] uppercase font-bold" style={{ color: "#E24A4A" }}>
              Virtual Plant Visit
            </div>
            <div className="mt-3 font-serif-display text-5xl font-extrabold" style={{ color: "#FFF3F2" }}>
              80%
            </div>
            <p className="mt-2 text-sm" style={{ color: "#B9B4AC" }}>
              lead conversion, in 3 months not 8.
            </p>
          </div>
        </div>
      ) : (
        <div className="p-8 md:p-10 grid md:grid-cols-5 gap-6 items-center">
          <div className="md:col-span-3">
            <div
              className="text-[0.6rem] tracking-[0.3em] uppercase font-bold"
              style={{ color: o.accent }}
            >
              By invitation only
            </div>
            <h3
              className="mt-4 font-serif-display text-5xl font-bold leading-[0.98]"
              style={{ color: o.text }}
            >
              Stand Out.<br />
              <span style={{ color: o.accent }}>Sell More.</span>
            </h3>
            <p className="mt-4 text-sm max-w-sm" style={{ color: o.muted }}>
              Build trust and close deals faster than ever. Turn international
              exhibitions into signed export orders.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span
                className="px-5 py-2.5 rounded-full text-sm font-semibold"
                style={{ background: o.accent, color: o.btnText }}
              >
                Book a Strategy Call
              </span>
              <span
                className="px-5 py-2.5 rounded-full text-sm font-semibold border"
                style={{ borderColor: o.border, color: o.text }}
              >
                Explore Solutions
              </span>
            </div>
          </div>
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            {[
              ["26+", "Years"],
              ["80%", "Conversion"],
              ["2x", "Exports"],
              ["100s", "Clients"],
            ].map(([v, l]) => (
              <div
                key={l}
                className="rounded-xl p-4 text-center border"
                style={{ background: o.panel, borderColor: o.border }}
              >
                <div className="font-serif-display text-2xl font-bold" style={{ color: o.accent }}>
                  {v}
                </div>
                <div className="text-[0.7rem] mt-1" style={{ color: o.muted }}>
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ThemePreview() {
  return (
    <main className="pt-28 pb-24 px-6 lg:px-10 max-w-6xl mx-auto">
      <h1 className="font-serif-display text-3xl font-bold text-center">
        Choose your look
      </h1>
      <p className="text-center text-[var(--muted)] mt-3">
        Four palette directions. Same content, same modern font. Red is used only as a small accent.
      </p>

      <div className="mt-14 space-y-12">
        {options.map((o) => (
          <div key={o.id}>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-serif-display text-2xl font-bold text-[var(--gold)]">
                Option {o.id}
              </span>
              <span className="font-semibold">{o.name}</span>
              <span className="text-sm text-[var(--muted)]">{o.note}</span>
            </div>
            <Mock o={o} />
          </div>
        ))}
      </div>
    </main>
  );
}
